import { useState } from 'react'
import { PRODUCT_ENTITLEMENTS } from '@headline/schemas'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { PageHeader } from '@/components/PageHeader'
import { NewRtaDialog } from '@/components/rtas/NewRtaDialog'
import { RtaTable } from '@/components/rtas/RtaTable'
import { Button } from '@/components/ui/button'
import { accountQuery } from '@/lib/queries'
import { rtasQuery } from '@/lib/rta-queries'

/**
 * An account's RTAs, across its clients — the Reports list's twin. An RTA binds to a client, so
 * creating one picks the client, then opens its Topics tab.
 */
export const Route = createFileRoute('/app/accounts/$accountId_/rtas/')({
  loader: async ({ context, params }) => {
    // RTAs are a product: an account that didn't buy them has no RTA surface (the sidebar hides it
    // too). The UI gates everyone on the entitlement, staff included, so a toggle shows at once.
    const account = await context.queryClient.ensureQueryData(accountQuery(params.accountId))
    if (!account.entitlements.includes(PRODUCT_ENTITLEMENTS.rta)) {
      throw redirect({ to: '/app/accounts/$accountId', params: { accountId: params.accountId } })
    }
    await context.queryClient.ensureQueryData(rtasQuery({ accountId: params.accountId }))
  },
  component: AccountRtasPage,
})

function AccountRtasPage() {
  const { accountId } = Route.useParams()
  const { data: account } = useSuspenseQuery(accountQuery(accountId))
  const { data } = useSuspenseQuery(rtasQuery({ accountId }))
  const [creating, setCreating] = useState(false)

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="RTAs"
        description={`${account.name}'s real-time alerts. Each listens on topics and posts what an IA sends to Slack.`}
        className="mb-0"
        actions={<Button onClick={() => setCreating(true)}>New RTA</Button>}
      />
      {data.rtas.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No RTAs for {account.name} yet. Create one with the button above.
        </p>
      ) : (
        <RtaTable rtas={data.rtas} showClient />
      )}
      {creating && (
        <NewRtaDialog
          accounts={[{ id: accountId, name: account.name }]}
          onClose={() => setCreating(false)}
        />
      )}
    </div>
  )
}
